'use client'

import type { LandingEarnItem, LandingLendItem } from '@/app/api/landing/markets/types'
import { hyperEVM } from '@/config/chain/constants'
import { E_EARN_VAULT_CHAIN_ID, E_LEND_CHAIN_ID } from '@/config/consts'
import { paths } from '@/config/paths'
import { getELendBackSource } from '@/features/elend/utils/getELendBackSource'
import { Skeleton } from '@/ui/atoms/skeleton/Skeleton'
import { TokenImage } from '@/ui/atoms/token-image/TokenImage'
import { Typography } from '@/ui/atoms/typography/Typography'
import Link from 'next/link'
import { formatTVL } from '../utils/formatTVL'

interface CombinedEarnCardContentProps {
  lendItems: LandingLendItem[]
  earnItems: LandingEarnItem[]
  isLoading: boolean
  limit?: number
}

interface NormalizedItem {
  key: string
  href: string
  assetSymbol: string
  tokenImageUrl: string
  fallbackLogoURI?: string
  sourceLabel?: string
  tvlUsd: number
  apy: number
}

function normalizeLendItem(item: LandingLendItem): NormalizedItem {
  const href =
    item.source === 'elend'
      ? `${paths.eLendDetails
          .replace(':chainId', E_LEND_CHAIN_ID.toString())
          .replace(':vaultAddress', item.address)}?from=${getELendBackSource(item.sourceMarketKey) ?? 'earn'}`
      : `${paths.pooledDetails.replace(':chainId', hyperEVM.id.toString()).replace(':asset', item.address)}?from=pooled`

  return {
    key: `lend-${item.id}`,
    href,
    assetSymbol: item.assetSymbol,
    tokenImageUrl: item.tokenImageUrl,
    fallbackLogoURI: item.fallbackLogoURI,
    sourceLabel: item.sourceLabel,
    tvlUsd: item.tvlUsd,
    apy: item.apy,
  }
}

function normalizeEarnItem(item: LandingEarnItem): NormalizedItem {
  return {
    key: `earn-${item.id}`,
    href: paths.lendDetails
      .replace(':chainId', E_EARN_VAULT_CHAIN_ID.toString())
      .replace(':vaultAddress', item.address),
    assetSymbol: item.assetSymbol,
    tokenImageUrl: item.tokenImageUrl,
    fallbackLogoURI: item.fallbackLogoURI,
    sourceLabel: 'Vault',
    tvlUsd: item.tvlUsd,
    apy: item.supplyAPY,
  }
}

export function CombinedEarnCardSkeleton() {
  return (
    <div className="-mx-2 flex flex-col gap-4">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-2 py-1.5">
          <Skeleton className="h-8 w-8 rounded-full" />
          <div className="flex flex-1 flex-col gap-1">
            <div className="flex items-center gap-1.5">
              <Skeleton className="h-4 w-16" />
              <Skeleton className="h-4 w-10 rounded" />
            </div>
            <Skeleton className="h-3 w-20" />
          </div>
          <Skeleton className="h-5 w-14" />
        </div>
      ))}
    </div>
  )
}

export function CombinedEarnCardContent({ lendItems, earnItems, isLoading, limit = 4 }: CombinedEarnCardContentProps) {
  if (isLoading && lendItems.length === 0 && earnItems.length === 0) {
    return <CombinedEarnCardSkeleton />
  }

  const merged = [...lendItems.map(normalizeLendItem), ...earnItems.map(normalizeEarnItem)]
    .sort((a, b) => b.apy - a.apy)
    .slice(0, limit)

  return (
    <div className="-mx-2 flex flex-col gap-4">
      {merged.map((item) => (
        <Link
          key={item.key}
          href={item.href}
          onClick={(event) => event.stopPropagation()}
          className="flex items-center gap-3 rounded-sm px-2 py-1.5 transition-colors hover:bg-white/5"
        >
          <TokenImage
            src={item.tokenImageUrl}
            alt={item.assetSymbol}
            width={32}
            height={32}
            className="h-8 w-8 shrink-0 rounded-full"
            fallbackSrc={item.fallbackLogoURI}
          />
          <div className="flex min-w-0 flex-1 flex-col">
            <div className="flex items-center gap-1.5">
              <span className="truncate font-semibold text-sm text-white">{item.assetSymbol}</span>
              {item.sourceLabel && (
                <span className="shrink-0 rounded bg-white/10 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                  {item.sourceLabel}
                </span>
              )}
            </div>
            <span className="text-[11px] text-muted-foreground">{formatTVL(item.tvlUsd)} TVL</span>
          </div>
          <Typography variant="span" gradient className="font-bold text-lg" style={{ paddingBottom: '0' }}>
            {item.apy > 0 ? `${item.apy.toFixed(2)}%` : '0.00%'}
          </Typography>
        </Link>
      ))}
    </div>
  )
}
